/**
 * Use API Keys Hook
 *
 * Custom hooks for managing API keys with TanStack Query.
 * Handles caching, refetching, mutations, and error states automatically.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  fetchApiKeys,
  fetchApiKeyById,
  createApiKey,
  deactivateApiKey,
  updateApiKeyRateLimits,
  rotateApiKey,
  fetchRotationStatus,
  fetchRotationHistory,
  fetchRotationSummary,
} from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type {
  ApiKey,
  ApiKeyListParams,
  ApiKeyListResponse,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  DeactivateApiKeyResponse,
  UpdateRateLimitsRequest,
  RotationStatusResponse,
  RotationHistoryItem,
  RotateApiKeyResponse,
  RotationSummaryResponse,
} from '@/types/api-key';

/**
 * Use API Keys
 * Fetches paginated list of API keys with filters
 *
 * @param params - Query parameters for filtering and pagination
 * @returns TanStack Query result with API keys data
 */
export function useApiKeys(params: ApiKeyListParams): UseQueryResult<ApiKeyListResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['apiKeys', params],
    queryFn: async () => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchApiKeys(params, token);
    },
    staleTime: 30000, // 30 seconds - data is considered fresh for 30s
    gcTime: 300000, // 5 minutes - cache is garbage collected after 5 minutes
    retry: 2, // Retry failed requests twice
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnReconnect: true, // Refetch when network reconnects
  });
}

/**
 * Use API Key by ID
 * Fetches a single API key by its ID
 *
 * @param keyId - API key ID
 * @returns TanStack Query result with single API key data
 */
export function useApiKeyById(keyId: string | null): UseQueryResult<ApiKey> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['apiKey', keyId],
    queryFn: async () => {
      if (!keyId) {
        throw new Error('No API key ID provided');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchApiKeyById(keyId, token);
    },
    enabled: keyId !== null, // Only run query if ID is provided
    staleTime: 10000, // 10 seconds - individual keys change more frequently
    gcTime: 300000, // 5 minutes - keep in cache
    retry: 2,
  });
}

/**
 * Use Rotation Status
 * Fetches rotation status for a specific API key
 *
 * @param keyId - API key ID
 * @returns TanStack Query result with rotation status data
 */
export function useRotationStatus(keyId: string | null): UseQueryResult<RotationStatusResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['rotationStatus', keyId],
    queryFn: async () => {
      if (!keyId) {
        throw new Error('No API key ID provided');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchRotationStatus(keyId, token);
    },
    enabled: keyId !== null,
    staleTime: 30000,
    gcTime: 300000,
    retry: 2,
  });
}

/**
 * Use Rotation History
 * Fetches rotation history for a specific API key
 *
 * @param keyId - API key ID
 * @returns TanStack Query result with rotation history data
 */
export function useRotationHistory(keyId: string | null): UseQueryResult<RotationHistoryItem[]> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['rotationHistory', keyId],
    queryFn: async () => {
      if (!keyId) {
        throw new Error('No API key ID provided');
      }

      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchRotationHistory(keyId, token);
    },
    enabled: keyId !== null,
    staleTime: 30000,
    gcTime: 300000,
    retry: 2,
  });
}

/**
 * Use Rotation Summary
 * Fetches overall rotation summary statistics
 *
 * @returns TanStack Query result with rotation summary data
 */
export function useRotationSummary(): UseQueryResult<RotationSummaryResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: ['rotationSummary'],
    queryFn: async () => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return fetchRotationSummary(token);
    },
    staleTime: 60000, // 1 minute - summary data changes less frequently
    gcTime: 300000,
    retry: 2,
    refetchOnWindowFocus: true,
  });
}

/**
 * Use Create API Key
 * Mutation hook for creating a new API key
 *
 * @returns TanStack Mutation result
 */
export function useCreateApiKey(): UseMutationResult<
  CreateApiKeyResponse,
  Error,
  CreateApiKeyRequest
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateApiKeyRequest) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return createApiKey(data, token);
    },
    onSuccess: () => {
      // Invalidate API keys list and rotation summary to refetch with new key
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['rotationSummary'] });
    },
  });
}

/**
 * Use Deactivate API Key
 * Mutation hook for deactivating an API key
 *
 * @returns TanStack Mutation result
 */
export function useDeactivateApiKey(): UseMutationResult<DeactivateApiKeyResponse, Error, string> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (keyId: string) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return deactivateApiKey(keyId, token);
    },
    onSuccess: (_data, keyId) => {
      // Invalidate both the list, the specific key, and rotation summary
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['apiKey', keyId] });
      queryClient.invalidateQueries({ queryKey: ['rotationSummary'] });
    },
  });
}

/**
 * Use Update API Key Rate Limits
 * Mutation hook for updating rate limits on an API key
 *
 * @returns TanStack Mutation result
 */
export function useUpdateApiKeyRateLimits(): UseMutationResult<
  ApiKey,
  Error,
  { keyId: string; data: UpdateRateLimitsRequest }
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ keyId, data }: { keyId: string; data: UpdateRateLimitsRequest }) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return updateApiKeyRateLimits(keyId, data, token);
    },
    onSuccess: updatedKey => {
      // Invalidate both the list and the specific key query
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['apiKey', updatedKey.id] });
    },
  });
}

/**
 * Use Rotate API Key
 * Mutation hook for rotating an API key
 *
 * @returns TanStack Mutation result
 */
export function useRotateApiKey(): UseMutationResult<RotateApiKeyResponse, Error, string> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (keyId: string) => {
      const token = getIdToken();

      if (!token) {
        throw new Error('No ID token available. Please log in again.');
      }

      return rotateApiKey(keyId, token);
    },
    onSuccess: data => {
      // Invalidate list, old key, new key, rotation status/history, and summary
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      queryClient.invalidateQueries({ queryKey: ['apiKey', data.data.oldKeyId] });
      queryClient.invalidateQueries({ queryKey: ['apiKey', data.data.newKeyId] });
      queryClient.invalidateQueries({ queryKey: ['rotationStatus', data.data.oldKeyId] });
      queryClient.invalidateQueries({ queryKey: ['rotationHistory', data.data.oldKeyId] });
      queryClient.invalidateQueries({ queryKey: ['rotationSummary'] });
    },
  });
}
